/**
 * What a herb actually does (M4-03) — the Meddygon Myddfai layer.
 *
 * The design claim of the ticket is that medicine is *knowledge*, not loot, and
 * the shape of this module is that claim written as types. There is no potion
 * here and nothing that heals: every field below is a **modifier on a process
 * the body already has** — the staunch roll, the infection risk, the pain a
 * character is carrying, the toughness roll an infected man makes each day.
 * Nothing an entry can say is capable of skipping a clock, because the schema
 * gives it no way to say so. A player who learns what yarrow does has learned
 * something true about yarrow.
 *
 * The split is `wound.ts`'s: **the sim owns the schema, content owns every
 * number.** `@annwn/content` declares the pharmacopoeia and this module declares
 * what a declaration may contain and what reads it, so a balance pass or a
 * post-MVP herb touches no rule code. The lookup goes through the catalogue like
 * every other piece of content, which is why a world with no herb table simply
 * has herbs that are dressings and nothing more — `NO_EFFECT` is a real answer,
 * not a missing one.
 */
/** What a material with nothing authored about it is worth: exactly nothing. */
export const NO_EFFECT = { id: '', learned: '' };
/** Tag every prepared or gathered thing a body can take *internally* carries. */
export const REMEDY_TAG = 'remedy';
/** Tags the treatment verbs ask about when they go looking for a material. */
export const POULTICE_TAG = 'poultice';
export const DRESSING_TAG = 'dressing';
export const ANTISEPTIC_TAG = 'antiseptic';
/**
 * What this item is worth as medicine, or `null` if it is not.
 *
 * `null` rather than `NO_EFFECT` so a caller can tell "linen, which is honestly
 * just clean cloth" from "a herb whose entry the content pass forgot" — the
 * first is the baseline everything is measured against and the second is a
 * content bug. Callers that only want the numbers use `effectOf`.
 */
export function herbEffect(source, defId) {
    return source.herb?.(defId) ?? null;
}
/** The same, defaulted — for arithmetic that does not care which it got. */
export function effectOf(source, defId) {
    return herbEffect(source, defId) ?? NO_EFFECT;
}
/**
 * How good a material is as a dressing, as one number to sort by.
 *
 * A single score rather than a priority list of tags, because the ladder the
 * ticket describes is genuinely multi-dimensional — honey buys infection, moss
 * buys absorbency, yarrow buys dice — and a chain of `if (poultice)` would fix
 * the ordering in code the day content wants to change it. Dice are weighted
 * heaviest because a die is worth about three blood a success (`BLEED_PER_
 * SUCCESS`), and the infection term is a *reduction*, so `1 − factor` is what it
 * is worth.
 */
export function dressingValue(effect) {
    return (effect.dice ?? 0) * 2 + (effect.bleed ?? 0) + (1 - (effect.infection ?? 1)) * 2;
}
/** Is this something a body takes internally — chewed, or drunk (M4-03)? */
export function isRemedy(def) {
    return def.tags.includes(REMEDY_TAG);
}
/** Does this remedy actually do anything once taken? */
export function relieves(effect) {
    return (effect.pain ?? 0) > 0 || (effect.fever ?? 0) > 0;
}
/* -------------------------------------------------------------------------- *
 * What a character has learned
 * -------------------------------------------------------------------------- */
/**
 * The line to append to this item's description for this character, or `null`.
 *
 * The whole of M4-03's discoverability, and it is deliberately one function: a
 * herb's effect text is *not* a property of the herb, it is a property of the
 * pair (herb, person). Two characters looking in the same satchel read different
 * sentences off the same moss, which is what "medicine as knowledge" means when
 * it has to be true of a data structure rather than of a design document.
 */
export function learnedNote(effect, lore) {
    if (!effect || effect.learned.length === 0)
        return null;
    return lore?.remedies?.includes(effect.id) === true ? effect.learned : null;
}
//# sourceMappingURL=herbs.js.map